import { useCallback, useMemo } from 'react';
import { useMytaverse } from '@providers/MytaverseProvider';

import { AvaturnStatuses } from '@features/avaturn/interfaces';

import {
  IXRHrBrandTypes,
  IXRHrCredStatusTypes,
  IXRHrPersonaTypes,
  ChangeAvatarTypes,
  WebsocketAction,
} from '@clients/ek/interfaces';
import { useAvaturnHelperType } from '@features/avaturn/pages/Avaturn/interfaces';
import ParticipantsService from '@services/ParticipantsService';
import { AvatarTypes } from '@interfaces/avatar';
import AvaturnService from '@features/avaturn/services/AvaturnService';
import AvaturnServiceEk from '@clients/ek/features/avaturn/services/AvaturnService';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';
import { EventRole } from '@interfaces/event';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useClientEventContext } from '@clients/ek/providers/EventProvider/EventProvider';
import useUserProfileHelper, {
  getAvaturnBrand,
} from '@clients/ek/features/avaturn/hooks/useUserProfileHelper';
import { Gender } from '@avaturn/sdk';
import { IAssetsParams } from '@clients/ek/features/avaturn/interfaces';

export const getAvaturnPersona = (props: {
  brand: string;
  persona: string;
  reason?: string;
}) => {
  const { brand, persona, reason } = props;

  if (brand === IXRHrBrandTypes.SD) {
    const newPersona =
      persona.toUpperCase() === IXRHrPersonaTypes.Purser
        ? IXRHrPersonaTypes.Purser
        : IXRHrPersonaTypes.CabinCrew;

    switch (reason) {
      case ChangeAvatarTypes.UniformHalf: {
        return `${newPersona}|${reason}`;
      }
      case ChangeAvatarTypes.UniformFull: {
        return newPersona;
      }
      case ChangeAvatarTypes.UniformFullSubsequent: {
        return `${newPersona}|${reason}`;
      }
    }

    if (persona.toUpperCase() === IXRHrPersonaTypes.Abinitio) {
      return IXRHrPersonaTypes.Abinitio;
    }

    return persona;
  }

  return undefined;
};

const useAvaturnHelper: useAvaturnHelperType = () => {
  const { user, configs, sendMessageToUnreal } = useMytaverse();

  const { clientSpecificData, getProcessedBrand, getGender } =
    useUserProfileHelper();

  const { showAvaturnModal, currentEventParticipant, setShowAvaturnModal } =
    useMytaverseEvent();
  const { setRecreateAvaturnAvatar } = useClientEventContext();

  const hasUniformed = useMemo(() => {
    return !!user?.avatars.find(({ tags = [] }) => tags.includes('UNIFORM'));
  }, [user?.avatars]);

  const getToken = useCallback(() => {
    const { ixrCredentialStatus, brand } = clientSpecificData;

    if (!brand || !ixrCredentialStatus) {
      console.log('Avaturn: getToken Missing required parameters');
      return null;
    }

    if (
      configs?.avaturn?.useSelfHosted &&
      configs?.avaturn?.tokens?.selfHosted
    ) {
      return configs?.avaturn?.tokens?.selfHosted;
    }

    if (ixrCredentialStatus.toUpperCase() === IXRHrCredStatusTypes.Pre) {
      return configs?.avaturn?.tokens?.ek;
    } else {
      switch (brand.toUpperCase()) {
        case IXRHrBrandTypes.Dnata:
          return configs?.avaturn?.tokens?.dnata;

        case IXRHrBrandTypes.Marhaba:
          return configs?.avaturn?.tokens?.marhaba;

        case IXRHrBrandTypes.Ekas:
          return configs?.avaturn?.tokens?.ekas;
      }
    }

    return configs?.avaturn?.tokens?.ek;
  }, [clientSpecificData, configs]);

  const getAssetParams = useCallback(
    (
      withPersona = false,
      reason: string = ChangeAvatarTypes.UniformHalf,
    ): IAssetsParams => {
      const { gender, brand, persona } = clientSpecificData;

      if (!withPersona && currentEventParticipant?.isDemo) {
        return {
          credentialStatus: IXRHrCredStatusTypes.FT,
          gender,
          brand: IXRHrBrandTypes.Demo,
        };
      }

      if (
        brand === IXRHrBrandTypes.SD &&
        !withPersona &&
        currentEventParticipant?.role &&
        [EventRole.Admin, EventRole.Coordinator].includes(
          currentEventParticipant?.role,
        )
      ) {
        return {
          credentialStatus: IXRHrCredStatusTypes.FT,
          gender,
          brand: IXRHrBrandTypes.SD_FACILITATOR,
        };
      }

      let finalReason = reason;
      if (
        withPersona &&
        reason === ChangeAvatarTypes.UniformHalf &&
        hasUniformed
      ) {
        finalReason = ChangeAvatarTypes.UniformFullSubsequent;
      }

      return {
        credentialStatus: clientSpecificData.ixrCredentialStatus,
        gender,
        brand,
        persona: getAvaturnPersona({
          brand,
          persona,
          reason: withPersona && finalReason ? finalReason : undefined,
        }),
      };
    },
    [
      clientSpecificData,
      hasUniformed,
      currentEventParticipant?.isDemo,
      currentEventParticipant?.role,
    ],
  );

  const getChangeAvatarParams = useCallback(
    (reason?: string) => {
      const params = {
        ixrCredentialStatus:
          clientSpecificData?.ixrCredentialStatus?.toUpperCase(),
        gender: clientSpecificData?.gender.toUpperCase(),
        brand: clientSpecificData?.brand.toUpperCase(),
        persona: clientSpecificData?.persona.toUpperCase(),
        tags: ['UNIFORM'],
      };

      if (reason === ChangeAvatarTypes.UniformTryOn) {
        //Abinitio will try on Cabin_Crew attire
        params.persona =
          clientSpecificData?.persona?.toUpperCase() ===
          IXRHrPersonaTypes.Purser
            ? IXRHrPersonaTypes.Purser
            : IXRHrPersonaTypes.CabinCrew;

        params.persona += `|${ChangeAvatarTypes.UniformTryOn}`;
      }

      const brand = getAvaturnBrand(
        clientSpecificData.ixrCredentialStatus,
        clientSpecificData.brand,
      );

      return { ...params, brand };
    },
    [clientSpecificData, getProcessedBrand],
  );

  const deleteAvatar = useCallback(async (avatarId: string, brand: string) => {
    const avatars = await ParticipantsService.getParticipantAvaturnAvatars();

    const avatar = avatars.find((item) => item.avatarId === avatarId);

    let listOfAvatars =
      await ParticipantsService.deleteParticipantAvatarFromList({
        avatarType: AvatarTypes.AVATURN,
        avatarId,
        brand,
        hardDelete: true,
      });

    if (avatar?.changeAssets?.childAvaturnId) {
      try {
        const childAvatar = await AvaturnService.getAvatarInfo(
          avatar.changeAssets.childAvaturnId,
        );

        const isAvatarAttached = avatars.find(
          (item) => item.avatarId === childAvatar.avatarId,
        );

        if (!isAvatarAttached) {
          await ParticipantsService.deleteParticipantAvatarFromList({
            avatarType: AvatarTypes.AVATURN,
            avatarId: childAvatar.avatarId,
            brand,
            hardDelete: true,
          });
        }
      } catch (error) {
        MytaverseLogger.error(
          `childAvaturn deletion: ${getCatchErrorMessage(error)}`,
        );
      }
    }

    const limitedAvatars = avatars.filter(
      (avatar) =>
        avatar?.status === AvaturnStatuses.LIMITED &&
        avatar?.changeAssets?.baseAvatarId === avatarId &&
        avatar.tags?.includes('UNIFORM'),
    );

    if (limitedAvatars.length > 0) {
      for (const avatar of limitedAvatars) {
        listOfAvatars =
          await ParticipantsService.deleteParticipantAvatarFromList({
            avatarType: AvatarTypes.AVATURN,
            avatarId: avatar.avatarId,
            brand,
            hardDelete: true,
          });
      }
    }

    return listOfAvatars;
  }, []);

  const exportPreprocess = useCallback(
    async (avaturnId: string, avatarId = '') => {
      if (!avatarId) {
        try {
          return await AvaturnService.deleteAvaturn(avaturnId);
        } catch (e) {
          MytaverseLogger.log(
            `deleteAvaturn error: ${getCatchErrorMessage(e)}`,
          );
        }
      }

      const param = getChangeAvatarParams(ChangeAvatarTypes.UniformTryOn);

      if (param.brand !== IXRHrBrandTypes.SD) {
        try {
          return await AvaturnService.deleteAvaturn(avaturnId);
        } catch (e) {
          MytaverseLogger.log(
            `deleteAvaturn error: ${getCatchErrorMessage(e)}`,
          );
        }
      }

      try {
        const result = await AvaturnServiceEk.cloneAvatar({
          ...param,
          avatarId,
          tags: [ChangeAvatarTypes.UniformTryOn],
          renderPic: false,
        });
        MytaverseLogger.log(`cloneAvatar: ${JSON.stringify(result)}`);
      } catch (e) {
        MytaverseLogger.log(`cloneAvatar error: ${getCatchErrorMessage(e)}`);
      }
    },
    [getChangeAvatarParams],
  );

  const handleBackBtnClick = useCallback(() => {
    if (showAvaturnModal) {
      sendMessageToUnreal({
        action: WebsocketAction.CloseMagicMirror,
        payload: false,
      });
      setShowAvaturnModal(false);
      return true;
    }

    return false;
  }, [showAvaturnModal]);

  const handleExportAvatar = useCallback(
    ({
      url,
      gender,
      avaturnId,
      avatarId,
      cb,
    }: {
      url: string;
      gender: Gender;
      avaturnId: string;
      avatarId: string;
      cb?: () => void;
    }) => {
      if (showAvaturnModal) {
        sendMessageToUnreal({ action: WebsocketAction.AvatarSavedMagicMirror });
        cb?.();

        setRecreateAvaturnAvatar({
          url,
          gender,
          avaturnId,
          avatarId,
        });
        setShowAvaturnModal(false);

        return true;
      }

      return false;
    },
    [showAvaturnModal],
  );

  return {
    getProcessedBrand,
    getGender,
    getToken,
    getAssetParams,
    getChangeAvatarParams,
    deleteAvatar,
    exportPreprocess,
    handleBackBtnClick,
    handleExportAvatar,
  };
};

export default useAvaturnHelper;
